'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Calendar, TrendingUp } from 'lucide-react';
import { useQuery } from '@apollo/client/react';
import { endOfMonth, format, startOfMonth, startOfYear, subMonths } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  INVOICE_TOTALS_QUERY,
  type InvoiceTotals,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { Skeleton } from '@paeffgen/react/crm-documents/ui';
import { formatCurrency, formatNumber } from '@paeffgen/react/crm-documents/util';

type PeriodId = 'MONTH' | 'PREV_MONTH' | 'YEAR';

const PERIODS: Array<{ id: PeriodId; label: string }> = [
  { id: 'MONTH', label: 'Monat' },
  { id: 'PREV_MONTH', label: 'Vormonat' },
  { id: 'YEAR', label: 'Jahr' },
];

/**
 * Zeitraum-Metadaten für die drei Kachel-Presets: Query-Filtergrenzen
 * (ISO yyyy-MM-dd) plus Anzeige-Texte (Scope-Zeile + Zeitraum-Fakt).
 * dateTo bleibt bei offenen Zeiträumen (laufender Monat/Jahr) undefined,
 * damit Kachel und Beleg-Liste (nur startDate im Link) dieselbe
 * Query-Variable teilen und aus demselben Apollo-Cache-Eintrag lesen;
 * angezeigt wird als Zeitraum-Ende dann das heutige Datum.
 */
function periodMeta(period: PeriodId): {
  dateFrom: string;
  dateTo?: string;
  scopeLabel: string;
  rangeText: string;
} {
  const now = new Date();
  const range = (from: Date, to: Date) =>
    `${format(from, 'dd.MM.')} – ${format(to, 'dd.MM.yyyy')}`;

  switch (period) {
    case 'MONTH': {
      const from = startOfMonth(now);
      return {
        dateFrom: format(from, 'yyyy-MM-dd'),
        scopeLabel: format(now, 'MMMM yyyy', { locale: de }),
        rangeText: range(from, now),
      };
    }
    case 'PREV_MONTH': {
      const prev = subMonths(now, 1);
      const from = startOfMonth(prev);
      const to = endOfMonth(prev);
      return {
        dateFrom: format(from, 'yyyy-MM-dd'),
        dateTo: format(to, 'yyyy-MM-dd'),
        scopeLabel: format(prev, 'MMMM yyyy', { locale: de }),
        rangeText: range(from, to),
      };
    }
    case 'YEAR': {
      const from = startOfYear(now);
      return {
        dateFrom: format(from, 'yyyy-MM-dd'),
        scopeLabel: `Kalenderjahr ${now.getFullYear()}`,
        rangeText: range(from, now),
      };
    }
  }
}

/** Fakten-Box (Belege / Gutschriften / Zeitraum) im Stil der Design-Vorlage. */
function Fact({
  label,
  value,
  unit,
}: {
  label: string;
  value: string;
  unit?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5 px-4 py-2.5 bg-zinc-50 border border-zinc-200 rounded-[10px]">
      <span className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-zinc-400">
        {label}
      </span>
      <span className="text-[15px] font-bold text-secondary tabular-nums whitespace-nowrap">
        {value}
        {unit && (
          <span className="text-[13px] font-semibold text-zinc-500 ml-1">
            {unit}
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * Dashboard-Kachel „Rechnungssumme (netto)": Rechnungen minus Gutschriften,
 * serverseitig aggregiert (invoiceTotals-Query). Layout nach der
 * Claude-Design-Vorlage „Rechnungssumme Card": Icon-Chip + Scope-Zeile,
 * Segmented-Control (Monat/Vormonat/Jahr, Default Jahr), große Summe mit
 * abgesetztem €, Fakten-Boxen und CTA „Belege anzeigen" auf die passend
 * vorgefilterte Beleg-Liste (dort zeigt die Summenleiste dieselbe Zahl).
 *
 * Nur im Einzel-Debitor-Kontext sichtbar (Gating wie Favoriten/Materialanfrage).
 */
export function InvoiceTotalsTile() {
  const { adminQueryVars, isSingleCustomerContext } = useAdmin();
  const [period, setPeriod] = useState<PeriodId>('YEAR');

  const meta = useMemo(() => periodMeta(period), [period]);

  const { data, loading, error } = useQuery<{ invoiceTotals: InvoiceTotals }>(
    INVOICE_TOTALS_QUERY,
    {
      variables: {
        filter: meta.dateTo
          ? { dateFrom: meta.dateFrom, dateTo: meta.dateTo }
          : { dateFrom: meta.dateFrom },
        ...adminQueryVars,
      },
      fetchPolicy: 'cache-and-network',
      skip: !isSingleCustomerContext,
    },
  );

  if (!isSingleCustomerContext) return null;
  // Komfort-Kennzahl: bei Fehler still ausblenden statt Fehlerkachel
  if (error) return null;

  const totals = data?.invoiceTotals;

  const listHref = `/belege?type=RECHNUNG,GUTSCHRIFT&dateRange=CUSTOM&startDate=${meta.dateFrom}${
    meta.dateTo ? `&endDate=${meta.dateTo}` : ''
  }`;

  return (
    <div className="relative bg-white border border-zinc-200 rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.03),0_4px_12px_rgba(0,0,0,0.025)] overflow-hidden p-5 pb-4 md:px-7 md:pt-6 md:pb-5">
      <div className="absolute top-0 left-0 w-full h-1 bg-primary" />

      {/* Kopf: Icon-Chip + Label/Scope links, Zeitraum-Umschalter rechts */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-5">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="flex items-center justify-center w-[42px] h-[42px] rounded-[11px] bg-orange-50 text-primary shrink-0"
            aria-hidden="true"
          >
            <TrendingUp size={22} strokeWidth={1.8} />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.07em] text-zinc-500">
              Rechnungssumme (netto)
            </p>
            <p className="flex items-center gap-1.5 mt-0.5 text-sm font-semibold text-secondary">
              <Calendar size={14} strokeWidth={1.8} className="text-zinc-400 shrink-0" />
              {meta.scopeLabel}
            </p>
          </div>
        </div>
        <div
          className="inline-flex w-full md:w-auto bg-zinc-100 border border-zinc-200 rounded-[10px] p-[3px] shrink-0"
          role="group"
          aria-label="Zeitraum der Rechnungssumme"
        >
          {PERIODS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setPeriod(id)}
              aria-pressed={period === id}
              className={`flex-1 md:flex-none px-1 md:px-4 py-[7px] text-[13px] rounded-[7px] whitespace-nowrap transition-colors ${
                period === id
                  ? 'bg-white text-primary font-bold shadow-sm'
                  : 'text-zinc-500 font-semibold hover:text-secondary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Summe + Fakten */}
      <div className="mt-4 md:mt-6 md:mb-5 md:flex md:items-end md:justify-between md:gap-6">
        {!totals && loading ? (
          <Skeleton className="h-10 md:h-12 w-48" />
        ) : (
          <h3 className="text-[38px] md:text-[46px] font-bold leading-none tracking-tight tabular-nums text-secondary">
            {totals ? formatNumber(totals.netSum) : '–'}
            <span className="text-2xl md:text-3xl font-semibold text-zinc-500 ml-1.5">
              €
            </span>
          </h3>
        )}
        {totals && (
          <div className="grid grid-cols-1 gap-2.5 mt-4 md:mt-0 md:flex md:items-stretch">
            <Fact
              label="Belege"
              value={String(totals.invoiceCount)}
              unit={
                <>
                  <span className="md:hidden">Rechn.</span>
                  <span className="hidden md:inline">
                    {totals.invoiceCount === 1 ? 'Rechnung' : 'Rechnungen'}
                  </span>
                </>
              }
            />
            {totals.creditCount > 0 && (
              <Fact
                label="Gutschriften"
                value={String(totals.creditCount)}
                unit={`(${formatCurrency(totals.netCreditTotal)})`}
              />
            )}
            <Fact label="Zeitraum" value={meta.rangeText} />
          </div>
        )}
      </div>

      {/* Fußzeile: Legal-Hinweis + CTA (mobil: CTA oben, Hinweis darunter) */}
      <div className="mt-4 md:mt-0 flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-5">
        <p className="order-2 md:order-1 m-0 text-[11.5px] leading-normal text-zinc-400 border-t border-zinc-100 pt-3.5 md:border-0 md:pt-0 md:max-w-2xl">
          Unverbindliche Übersicht auf Basis der im Kundenportal verfügbaren
          Belege — keine buchhalterische oder steuerliche Auswertung, maßgeblich
          sind die Originalbelege.
        </p>
        <Link
          href={listHref}
          className="order-1 md:order-2 inline-flex items-center justify-center gap-2 h-11 px-5 w-full md:w-auto bg-primary hover:bg-primary-hover text-white rounded-[10px] text-[14.5px] font-bold transition-colors shrink-0"
        >
          Belege anzeigen
          <ArrowRight size={17} strokeWidth={2} />
        </Link>
      </div>
    </div>
  );
}
